import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getWorkoutTypeColor, getWorkoutTypeName, getWorkoutTypeTextColor } from '../../utils/workoutColors';
import { formatDistance as formatDistanceUtil, getUnitPreference, parsePace, formatPace, convertPace } from '../../utils/unitConversions';
import WorkoutModal from './WorkoutModal';
import WorkoutImporter from './WorkoutImporter';
import ConfirmDialog from '../ConfirmDialog';
import './CalendarView.css';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Get pace string from training paces (handles both string and object formats)
const getPaceString = (trainingPaces, paceType) => {
  if (!trainingPaces || !paceType) return null;
  const paceData = trainingPaces[paceType];
  if (!paceData) return null;
  if (typeof paceData === 'string') return paceData;
  if (typeof paceData === 'object') {
    return paceData.pace_km || paceData.pace || null;
  }
  return null;
};

// Format pace info for display (min/km, min/mi, kph, mph)
const formatPaceInfo = (trainingPaces, paceType) => {
  const paceStr = getPaceString(trainingPaces, paceType);
  if (!paceStr) return null;

  const paceData = parsePace(paceStr);
  if (!paceData) return null;

  const KM_TO_MILES = 0.621371;
  const MILES_TO_KM = 1.60934;

  if (paceData.isRange) {
    // Range pace like "5:00 - 5:30/km"
    const minPerKm = `${formatPace(paceData.start)}-${formatPace(paceData.end)}`;
    const minPerMi = `${formatPace(paceData.start * MILES_TO_KM)}-${formatPace(paceData.end * MILES_TO_KM)}`;
    const kphStart = 60 / paceData.end; // slower pace = slower speed
    const kphEnd = 60 / paceData.start; // faster pace = faster speed
    const kph = `${kphStart.toFixed(1)}-${kphEnd.toFixed(1)}`;
    const mph = `${(kphStart * KM_TO_MILES).toFixed(1)}-${(kphEnd * KM_TO_MILES).toFixed(1)}`;
    return { minPerKm, minPerMi, kph, mph };
  } else {
    // Single pace
    const minPerKm = formatPace(paceData.start);
    const minPerMi = formatPace(paceData.start * MILES_TO_KM);
    const speedKph = 60 / paceData.start;
    const kph = speedKph.toFixed(1);
    const mph = (speedKph * KM_TO_MILES).toFixed(1);
    return { minPerKm, minPerMi, kph, mph };
  }
};

// Format seconds to human-readable time (e.g., 20 -> "0:20", 90 -> "1:30", 3600 -> "1:00:00")
const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '0:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Strip distance prefix from workout description (e.g., "19.3km Easy Run" -> "Easy Run")
const stripDistancePrefix = (description) => {
  if (!description) return description;
  // Match patterns like "19.3km ", "25.0km ", "8km ", etc. at the start
  return description.replace(/^\d+\.?\d*km\s+/i, '');
};

// Group repeating step patterns for display (e.g., 6x [0:20 rep + 1:00 easy])
// Helper to get pace from step (backend may use 'pace' or 'pace_type')
const getStepPace = (step) => step.pace_type || step.pace || '';

// Check if a step is an "interval" type (rep, interval, threshold, marathon, active)
// These should come FIRST in a repeat pattern, not recovery/easy
const isIntervalStep = (step) => {
  const pace = getStepPace(step);
  const stepType = step.step_type || step.type || '';
  // Check pace zone - interval types
  if (['repetition', 'interval', 'threshold', 'marathon'].includes(pace.toLowerCase())) {
    return true;
  }
  // Check step type - interval/active types (not recovery, warmup, cooldown)
  if (['interval', 'active'].includes(stepType.toLowerCase())) {
    return true;
  }
  return false;
};

const groupRepeatingSteps = (steps) => {
  if (!steps || steps.length === 0) return [];

  // Create a signature for a step to compare (check both pace and pace_type)
  const getStepSignature = (step) => {
    const pace = getStepPace(step);
    return `${step.duration_type}|${step.duration_value}|${pace}`;
  };

  // Helper to find ONE repeating pattern in a slice of steps
  const findOnePattern = (stepsSlice) => {
    if (!stepsSlice || stepsSlice.length < 4) return null; // Need at least 2x2 for a pattern

    for (let startIdx = 0; startIdx < stepsSlice.length - 1; startIdx++) {
      // Skip starting positions where the first step is recovery/easy
      if (stepsSlice[startIdx] && !isIntervalStep(stepsSlice[startIdx])) {
        continue;
      }

      for (let endIdx = stepsSlice.length; endIdx > startIdx + 3; endIdx--) {
        const middleSteps = stepsSlice.slice(startIdx, endIdx);

        const maxPatternLen = Math.floor(middleSteps.length / 2);
        for (let patternLength = 2; patternLength <= maxPatternLen; patternLength++) {
          if (middleSteps.length % patternLength !== 0) continue;

          const pattern = middleSteps.slice(0, patternLength);

          if (!isIntervalStep(pattern[0])) {
            continue;
          }

          const patternSig = pattern.map(getStepSignature).join('||');
          let isRepeating = true;
          let repeatCount = 0;

          for (let i = 0; i < middleSteps.length; i += patternLength) {
            const chunk = middleSteps.slice(i, i + patternLength);
            const chunkSig = chunk.map(getStepSignature).join('||');
            if (chunkSig !== patternSig) {
              isRepeating = false;
              break;
            }
            repeatCount++;
          }

          if (isRepeating && repeatCount >= 2) {
            return {
              startIdx,
              endIdx,
              repeatCount,
              pattern
            };
          }
        }
      }
    }
    return null;
  };

  // Recursively process steps to find multiple patterns
  const processSteps = (stepsToProcess) => {
    if (!stepsToProcess || stepsToProcess.length === 0) return [];

    const found = findOnePattern(stepsToProcess);

    if (!found) {
      // No pattern found, return all as singles
      return stepsToProcess.map(step => ({ type: 'single', step }));
    }

    const result = [];

    // Recursively process leading steps
    const leadingSteps = stepsToProcess.slice(0, found.startIdx);
    result.push(...processSteps(leadingSteps));

    // Add the repeat block
    result.push({
      type: 'repeat',
      repeatCount: found.repeatCount,
      steps: found.pattern
    });

    // Recursively process trailing steps
    const trailingSteps = stepsToProcess.slice(found.endIdx);
    result.push(...processSteps(trailingSteps));

    return result;
  };

  return processSteps(steps);
};

const CalendarView = ({ trainingPlan, profile, onPlanUpdate, onCreatePlan, onOpenWorkoutBuilder, refreshTrigger }) => {
  const { authFetch } = useAuth();
  const [currentDate] = useState(new Date());
  const currentMonthRef = useRef(null);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [draggedWorkout, setDraggedWorkout] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [workoutModal, setWorkoutModal] = useState({ isOpen: false, mode: 'create', date: null, workout: null });
  const [customWorkouts, setCustomWorkouts] = useState([]);
  const [importerOpen, setImporterOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, workout: null });

  // Fetch custom workouts from database
  const fetchCustomWorkouts = useCallback(async () => {
    if (!authFetch) return;
    try {
      const response = await authFetch('/api/user/custom-workouts');
      if (response.ok) {
        const data = await response.json();
        setCustomWorkouts(data.workouts || []);
      }
    } catch (err) {
      console.error('Failed to fetch custom workouts:', err);
    }
  }, [authFetch]);

  // Fetch custom workouts on mount and when refreshTrigger changes
  useEffect(() => {
    fetchCustomWorkouts();
  }, [fetchCustomWorkouts, refreshTrigger]);

  // Get user's preferred distance unit (profile setting takes priority, then localStorage, then default 'km')
  const profileUnits = profile?.preferred_units || profile?.preferredUnits;
  // Map 'miles' to 'mi' for the utility function
  const unit = profileUnits === 'miles' ? 'mi' : (profileUnits || getUnitPreference());

  // Format distance with unit (e.g., "8.3mi" or "13.3km")
  const formatDistance = (distanceKm, decimals = 1) => {
    if (!distanceKm) return null;
    return `${formatDistanceUtil(distanceKm, unit, decimals)}${unit}`;
  };

  // Format distance value only (no unit suffix)
  const formatDistanceValue = (distanceKm, decimals = 1) => {
    if (!distanceKm) return null;
    return formatDistanceUtil(distanceKm, unit, decimals);
  };

  // Get recommended pace for a workout type
  const getRecommendedPace = (workoutType) => {
    if (!trainingPlan?.training_paces) return null;
    const paces = trainingPlan.training_paces;
    const paceMap = {
      'E': paces.easy,
      'L': paces.easy, // Long runs are at easy pace
      'M': paces.marathon,
      'T': paces.threshold,
      'I': paces.interval,
      'R': paces.repetition
    };
    const pace = paceMap[workoutType];
    if (!pace) return null;
    // Handle both object and string formats
    return typeof pace === 'object' ? pace.pace : pace;
  };

  // Get all workouts as arrays by date (supports multiple workouts per day)
  // Merges training plan workouts with custom workouts from database
  const workoutsByDate = useMemo(() => {
    const workouts = {};

    // Add training plan workouts
    if (trainingPlan?.weeks) {
      trainingPlan.weeks.forEach(week => {
        week.workouts?.forEach(workout => {
          if (workout.date) {
            if (!workouts[workout.date]) {
              workouts[workout.date] = [];
            }
            workouts[workout.date].push(workout);
          }
        });
      });
    }

    // Add custom workouts from database
    customWorkouts.forEach(customWorkout => {
      if (customWorkout.scheduled_date) {
        const date = customWorkout.scheduled_date;
        if (!workouts[date]) {
          workouts[date] = [];
        }
        // Convert to same format as plan workouts
        workouts[date].push({
          date: date,
          workout_type: customWorkout.workout_type || 'Custom',
          type: customWorkout.workout_type || 'Custom',
          description: customWorkout.workout_name || customWorkout.description,
          distance_km: customWorkout.distance_km,
          duration_minutes: customWorkout.duration_minutes,
          is_custom: true,
          custom_workout_id: customWorkout.id,
          garmin_workout_id: customWorkout.garmin_workout_id,
          garmin_synced: !!customWorkout.garmin_workout_id
        });
      }
    });

    return workouts;
  }, [trainingPlan, customWorkouts]);

  // Generate all months from training plan start to end for vertical scroll
  const allMonthsData = useMemo(() => {
    const today = new Date();
    let startMonth, endMonth;

    if (trainingPlan?.weeks?.length) {
      const allDates = trainingPlan.weeks
        .flatMap(w => (w.workouts || []).map(wo => wo.date))
        .filter(Boolean)
        .sort();
      if (allDates.length) {
        const first = new Date(allDates[0]);
        const last = new Date(allDates[allDates.length - 1]);
        startMonth = new Date(first.getFullYear(), first.getMonth(), 1);
        endMonth = new Date(last.getFullYear(), last.getMonth(), 1);
      }
    }

    if (!startMonth) {
      startMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      endMonth = new Date(today.getFullYear(), today.getMonth() + 5, 1);
    }

    const months = [];
    let cursor = new Date(startMonth);

    while (cursor <= endMonth) {
      const year = cursor.getFullYear();
      const month = cursor.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDay = (firstDay.getDay() + 6) % 7; // Monday-first
      const totalDays = lastDay.getDate();
      const prevMonthLastDay = new Date(year, month, 0).getDate();

      const days = [];
      for (let i = startDay - 1; i >= 0; i--) {
        days.push({ day: prevMonthLastDay - i, isCurrentMonth: false, date: new Date(year, month - 1, prevMonthLastDay - i) });
      }
      for (let i = 1; i <= totalDays; i++) {
        days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
      }
      const remainingInRow = (7 - (days.length % 7)) % 7;
      for (let i = 1; i <= remainingInRow; i++) {
        days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
      }

      months.push({
        key: `${year}-${month}`,
        label: `${MONTH_NAMES[month]} ${year}`,
        year,
        month,
        days,
        isCurrent: month === today.getMonth() && year === today.getFullYear()
      });

      cursor = new Date(year, month + 1, 1);
    }

    return months;
  }, [trainingPlan]);

  // Scroll to current month on mount
  useEffect(() => {
    if (currentMonthRef.current) {
      currentMonthRef.current.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }, []);

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is race day
  const isRaceDay = (date) => {
    if (!trainingPlan?.goal_race_date) return false;
    return formatDate(date) === trainingPlan.goal_race_date;
  };


  const handleDayClick = (dayData) => {
    const dateStr = formatDate(dayData.date);
    const workouts = workoutsByDate[dateStr];
    if (workouts && workouts.length > 0) {
      // Show first workout (clicking individual workouts will show that specific one)
      setSelectedWorkout({ ...workouts[0], dateStr });
    } else if (onOpenWorkoutBuilder) {
      // Open WorkoutBuilder for empty day with the date pre-filled
      onOpenWorkoutBuilder(dateStr);
    }
  };

  const closeWorkoutPopup = () => {
    setSelectedWorkout(null);
  };

  // Drag and drop handlers
  const handleDragStart = (e, workout, dateStr, workoutIndex) => {
    setDraggedWorkout({ ...workout, originalDate: dateStr, workoutIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', dateStr);
  };

  const handleDragOver = (e, dateStr) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(dateStr);
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = async (e, targetDateStr) => {
    e.preventDefault();
    setDragOverDate(null);

    if (!draggedWorkout || draggedWorkout.originalDate === targetDateStr) {
      setDraggedWorkout(null);
      return;
    }

    try {
      // Pass workout_type and workout_index to identify the specific workout
      const moveResponse = await authFetch('/api/user/training-plan/move-workout', {
        method: 'POST',
        body: JSON.stringify({
          from_date: draggedWorkout.originalDate,
          to_date: targetDateStr,
          workout_type: draggedWorkout.type,
          workout_index: draggedWorkout.workoutIndex,
          swap: false
        })
      });

      if (moveResponse.ok && onPlanUpdate) {
        // Fetch the updated plan from the backend to ensure consistency
        const planResponse = await authFetch('/api/user/training-plan');
        if (planResponse.ok) {
          const data = await planResponse.json();
          // The API returns { plan: { plan_data: {...}, is_active: ... } }
          // But the app expects the plan_data directly (with weeks array)
          if (data.plan?.plan_data) {
            onPlanUpdate(data.plan.plan_data);
          }
        }
      }
    } catch (error) {
      console.error('Failed to move workout:', error);
    }

    setDraggedWorkout(null);
  };

  const handleDragEnd = () => {
    setDraggedWorkout(null);
    setDragOverDate(null);
  };

  // CRUD Operations
  const openCreateModal = (dateStr) => {
    setWorkoutModal({ isOpen: true, mode: 'create', date: dateStr, workout: null });
    setSelectedWorkout(null);
  };

  const openEditModal = (workout, dateStr) => {
    setWorkoutModal({ isOpen: true, mode: 'edit', date: dateStr, workout });
    setSelectedWorkout(null);
  };

  const closeWorkoutModal = () => {
    setWorkoutModal({ isOpen: false, mode: 'create', date: null, workout: null });
  };

  // Helper to recalculate week stats
  const recalculateWeekStats = (workouts) => {
    const totalDistance = workouts.reduce((sum, w) => sum + (w.distance_km || 0), 0);
    const qualitySessions = workouts.filter(w =>
      w.type && !['E', 'Rest'].includes(w.type)
    ).length;
    return { total_distance_km: totalDistance, quality_sessions: qualitySessions };
  };

  const handleSaveWorkout = async (workoutData, mode) => {
    const endpoint = '/api/user/training-plan/workout';
    let method = mode === 'create' ? 'POST' : 'PUT';

    let response = await authFetch(endpoint, {
      method,
      body: JSON.stringify(workoutData)
    });

    // If PUT returns 404 (workout not found), retry with POST to create it
    // This handles editing workouts on dates outside the original plan
    if (!response.ok && method === 'PUT' && response.status === 404) {
      response = await authFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(workoutData)
      });
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Handle Pydantic validation errors (array of objects) and string errors
      let errorMessage = 'Failed to save workout';
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors
          errorMessage = errorData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      throw new Error(errorMessage);
    }

    const responseData = await response.json();

    // Update local state
    if (onPlanUpdate && trainingPlan) {
      const updatedPlan = { ...trainingPlan };

      if (mode === 'create') {
        // Add new workout to the appropriate week
        let added = false;
        updatedPlan.weeks = updatedPlan.weeks.map(week => {
          if (added) return week;
          const workoutDates = week.workouts?.map(w => w.date) || [];
          if (workoutDates.length > 0) {
            const minDate = Math.min(...workoutDates.map(d => new Date(d)));
            const maxDate = Math.max(...workoutDates.map(d => new Date(d)));
            const newDate = new Date(workoutData.date);
            if (newDate >= minDate && newDate <= maxDate) {
              added = true;
              const newWorkouts = [...(week.workouts || []), responseData.workout].sort((a, b) =>
                a.date.localeCompare(b.date)
              );
              return {
                ...week,
                workouts: newWorkouts,
                ...recalculateWeekStats(newWorkouts)
              };
            }
          }
          return week;
        });

        // If not added to any week, add to last week
        if (!added && updatedPlan.weeks.length > 0) {
          const lastWeekIdx = updatedPlan.weeks.length - 1;
          const newWorkouts = [...(updatedPlan.weeks[lastWeekIdx].workouts || []), responseData.workout].sort((a, b) =>
            a.date.localeCompare(b.date)
          );
          updatedPlan.weeks[lastWeekIdx] = {
            ...updatedPlan.weeks[lastWeekIdx],
            workouts: newWorkouts,
            ...recalculateWeekStats(newWorkouts)
          };
        }
      } else {
        // Update existing workout
        updatedPlan.weeks = updatedPlan.weeks.map(week => {
          const updatedWorkouts = week.workouts?.map(w =>
            w.date === workoutData.date ? { ...w, ...workoutData } : w
          ) || [];
          return {
            ...week,
            workouts: updatedWorkouts,
            ...recalculateWeekStats(updatedWorkouts)
          };
        });
      }

      onPlanUpdate(updatedPlan);
    }
  };

  // Handle importing workout from JSON
  const handleImportWorkout = async (workoutData) => {
    try {
      await handleSaveWorkout(workoutData, 'create');
    } catch (error) {
      console.error('Failed to import workout:', error);
      alert(`Failed to import workout: ${error.message}`);
    }
  };

  const handleDeleteWorkout = async (workout) => {
    // Check if this is a custom workout or a training plan workout
    if (workout.is_custom && workout.custom_workout_id) {
      // Delete custom workout
      const response = await authFetch(`/api/user/custom-workouts/${workout.custom_workout_id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete workout');
      }

      // Update local state - remove from customWorkouts
      setCustomWorkouts(prev => prev.filter(w => w.id !== workout.custom_workout_id));
    } else {
      // Delete training plan workout
      const date = workout.date || workout.dateStr;
      const workoutName = workout.workout_name;

      // Build URL with optional workout_name query param for specific deletion
      let url = `/api/user/training-plan/workout/${date}`;
      if (workoutName) {
        url += `?workout_name=${encodeURIComponent(workoutName)}`;
      }

      const response = await authFetch(url, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete workout');
      }

      // Update local state - filter by date AND workout_name to delete only the specific one
      if (onPlanUpdate && trainingPlan) {
        const updatedPlan = { ...trainingPlan };
        updatedPlan.weeks = updatedPlan.weeks.map(week => {
          const filteredWorkouts = week.workouts?.filter(w => {
            // If we have a workout_name, match both date and name
            if (workoutName) {
              return !(w.date === date && w.workout_name === workoutName);
            }
            // Fallback: match by date only (first one on that date)
            return w.date !== date;
          }) || [];
          return {
            ...week,
            workouts: filteredWorkouts,
            ...recalculateWeekStats(filteredWorkouts)
          };
        });
        onPlanUpdate(updatedPlan);
      }
    }
  };

  return (
    <div className="calendar-view">
      {/* Calendar Header */}
      <div className="calendar-header">
        <h2 className="month-year">{MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
        <div className="calendar-header-actions">
          {onCreatePlan && (
            <button className="btn-create-plan" onClick={onCreatePlan}>New Plan</button>
          )}
          <button className="btn-import-workout" onClick={() => setImporterOpen(true)} title="Import Workout from JSON">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
        </div>
      </div>

      {/* Sticky weekday headers */}
      <div className="calendar-weekdays">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
          <div key={day} className="weekday">{day}</div>
        ))}
      </div>

      {/* Scrollable multi-month calendar */}
      <div className="calendar-scroll-container">
        {allMonthsData.map((monthData) => (
          <div
            key={monthData.key}
            className="calendar-month-section"
            ref={monthData.isCurrent ? currentMonthRef : null}
          >
            <div className="calendar-month-label">{monthData.label}</div>
            <div className="calendar-grid">
              {monthData.days.map((dayData, index) => {
                const dateStr = formatDate(dayData.date);
                const workouts = workoutsByDate[dateStr] || [];
                const today = isToday(dayData.date);
                const raceDay = isRaceDay(dayData.date);
                const isDragOver = dragOverDate === dateStr;
                const isDragging = draggedWorkout?.originalDate === dateStr;

                return (
                  <div
                    key={index}
                    className={`calendar-day ${!dayData.isCurrentMonth ? 'other-month' : ''} ${today ? 'today' : ''} ${raceDay ? 'race-day' : ''} ${workouts.length > 0 ? 'has-workout' : ''} ${isDragOver ? 'drag-over' : ''} ${isDragging ? 'dragging' : ''}`}
                    onClick={() => handleDayClick(dayData)}
                    onDragOver={(e) => handleDragOver(e, dateStr)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, dateStr)}
                  >
                    <span className="day-number">{dayData.day}</span>
                    <button
                      className="day-add-workout"
                      onClick={(e) => {
                        e.stopPropagation();
                        setWorkoutModal({ isOpen: true, mode: 'create', date: dateStr, workout: null });
                      }}
                      title="Add workout"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                    {workouts.map((workout, wIdx) => (
                      <div
                        key={wIdx}
                        className="workout-indicator"
                        draggable
                        onDragStart={(e) => {
                          e.stopPropagation();
                          handleDragStart(e, workout, dateStr, wIdx);
                        }}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedWorkout({ ...workout, dateStr });
                        }}
                      >
                        <span
                          className="workout-badge"
                          style={{ backgroundColor: getWorkoutTypeColor(workout.type), color: getWorkoutTypeTextColor(workout.type) }}
                          title={workout.workout_name || workout.description || getWorkoutTypeName(workout.type)}
                        >
                          {workout.workout_name || stripDistancePrefix(workout.description) || workout.type}
                        </span>
                      </div>
                    ))}
                    {raceDay && <span className="race-badge">🏁 Race Day</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Workout Detail Popup */}
      {selectedWorkout && (
        <div className="workout-popup-overlay" onClick={closeWorkoutPopup}>
          <div className="workout-popup" onClick={e => e.stopPropagation()}>
            <button className="popup-close" onClick={closeWorkoutPopup}>×</button>
            <div
              className="popup-header"
              style={{ backgroundColor: getWorkoutTypeColor(selectedWorkout.type), color: getWorkoutTypeTextColor(selectedWorkout.type) }}
            >
              <span className="popup-type">{selectedWorkout.workout_name || selectedWorkout.description || getWorkoutTypeName(selectedWorkout.type)}</span>
              <span className="popup-date">
                {new Date(selectedWorkout.dateStr).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}
              </span>
            </div>
            <div className="popup-body">
              {selectedWorkout.description && (
                <div className="popup-description">
                  <span className="stat-label">Workout</span>
                  <p>{selectedWorkout.description}</p>
                </div>
              )}

              {selectedWorkout.steps && selectedWorkout.steps.length > 0 && (
                <div className="popup-steps">
                  <span className="stat-label">Workout Structure</span>
                  <div className="step-list-display">
                    {groupRepeatingSteps(selectedWorkout.steps).map((item, idx) => {
                      const trainingPaces = trainingPlan?.training_paces;
                      return item.type === 'repeat' ? (
                        <div key={idx} className="step-display-repeat">
                          <span className="repeat-count">{item.repeatCount}x</span>
                          <div className="repeat-steps">
                            {item.steps.map((step, sIdx) => {
                              const pace = getStepPace(step);
                              const paceInfo = pace ? formatPaceInfo(trainingPaces, pace) : null;
                              return (
                                <div key={sIdx} className="step-display-item">
                                  <span className="step-duration">
                                    {step.duration_type === 'distance'
                                      ? formatDistance(step.duration_value / 1000)  /* Convert meters to km */
                                      : formatDuration(step.duration_value)
                                    }
                                  </span>
                                  {pace && (
                                    <span className={`step-pace step-pace--${pace}`}>
                                      @ {pace}
                                      {paceInfo && (
                                        <span className="pace-details">
                                          {paceInfo.minPerKm}/km
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div key={idx} className="step-display-item">
                          <span className="step-duration">
                            {item.step.duration_type === 'distance'
                              ? formatDistance(item.step.duration_value / 1000)  /* Convert meters to km */
                              : formatDuration(item.step.duration_value)
                            }
                          </span>
                          {(() => {
                            const pace = getStepPace(item.step);
                            const paceInfo = pace ? formatPaceInfo(trainingPaces, pace) : null;
                            return pace ? (
                              <span className={`step-pace step-pace--${pace}`}>
                                @ {pace}
                                {paceInfo && (
                                  <span className="pace-details">
                                    {paceInfo.minPerKm}/km
                                  </span>
                                )}
                              </span>
                            ) : null;
                          })()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="popup-actions">
                <button
                  className="popup-btn popup-btn-edit"
                  onClick={() => openEditModal(selectedWorkout, selectedWorkout.dateStr)}
                >
                  Edit
                </button>
                <button
                  className="popup-btn popup-btn-delete"
                  onClick={() => {
                    setConfirmDialog({ isOpen: true, workout: selectedWorkout });
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workout Create/Edit Modal */}
      <WorkoutModal
        isOpen={workoutModal.isOpen}
        onClose={closeWorkoutModal}
        onSave={handleSaveWorkout}
        onDelete={handleDeleteWorkout}
        workout={workoutModal.workout}
        date={workoutModal.date}
        mode={workoutModal.mode}
        trainingPaces={trainingPlan?.training_paces}
        vdot={trainingPlan?.vdot}
      />

      {/* Workout Importer Modal */}
      <WorkoutImporter
        isOpen={importerOpen}
        onClose={() => setImporterOpen(false)}
        onImport={handleImportWorkout}
        selectedDate={new Date().toISOString().split('T')[0]}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Workout"
        message="Are you sure you want to delete this workout? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={async () => {
          try {
            await handleDeleteWorkout(confirmDialog.workout);
            closeWorkoutPopup();
          } catch (err) {
            console.error('Failed to delete workout:', err);
          }
          setConfirmDialog({ isOpen: false, workout: null });
        }}
        onCancel={() => setConfirmDialog({ isOpen: false, workout: null })}
      />
    </div>
  );
};

export default CalendarView;
